import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import consignmentService, {
  type ConsignmentReceiptLine,
} from '@/services/consignment.service';
import warehouseLocationService from '@/services/warehouseLocation.service';
import { useWarehouses } from '@/hooks/useInventory';
import { ItemSelect, type ItemLike } from '@/components/inventory/ItemSelect';
import api from '@/services/api.service';

const { Title, Text } = Typography;

type LineDraft = ConsignmentReceiptLine & { key: string };

const emptyLine = (): LineDraft => ({
  key: `new-${Date.now()}-${Math.random()}`,
  item_id: '',
  qty: 1,
  uom: 'PCS',
  heat_number: '',
  dimension: '',
  location_id: null,
});

function unwrapList<T>(res: unknown): T[] {
  const body = (res as { data?: unknown })?.data ?? res;
  const list = (body as { data?: unknown })?.data ?? body;
  return Array.isArray(list) ? (list as T[]) : [];
}

const ConsignmentReceiptDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const warehouseId = Form.useWatch('warehouse_id', form);
  const { data: warehouses = [] } = useWarehouses();

  const { data: receipt, isLoading } = useQuery({
    queryKey: ['consignment-receipt', id],
    queryFn: () => consignmentService.getReceipt(id!),
    enabled: !isNew && !!id,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['warehouse-locations', warehouseId],
    queryFn: () => warehouseLocationService.listByWarehouse(warehouseId),
    enabled: !!warehouseId,
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['procurement-suppliers'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      return unwrapList<{
        id: string;
        supplier_code?: string;
        supplier_name?: string;
        is_active?: boolean;
      }>(res).filter((s) => s.is_active !== false);
    },
  });

  useEffect(() => {
    if (!receipt) return;
    form.setFieldsValue({
      supplier_id: receipt.supplier_id,
      warehouse_id: receipt.warehouse_id,
      receipt_date: receipt.receipt_date ? dayjs(receipt.receipt_date) : dayjs(),
      remarks: receipt.remarks,
    });
    setLines(
      (receipt.lines || []).map((l) => ({
        ...l,
        key: l.id || `ln-${l.line_number}`,
      }))
    );
  }, [receipt, form]);

  const binOptions = useMemo(
    () =>
      locations
        .filter((l) => l.is_active !== false)
        .map((l) => ({
          value: l.id,
          label: `${l.location_code} — ${l.location_name}`,
        })),
    [locations]
  );

  const isDraft = isNew || receipt?.status === 'Draft';

  const createMut = useMutation({
    mutationFn: async () => {
      const values = await form.validateFields();
      const payloadLines = lines
        .filter((l) => l.item_id && Number(l.qty) > 0)
        .map((l, i) => ({
          item_id: l.item_id,
          description: l.description || null,
          qty: Number(l.qty),
          uom: l.uom || 'PCS',
          heat_number: l.heat_number || null,
          dimension: l.dimension || null,
          serial_number: l.serial_number || null,
          batch_number: l.batch_number || null,
          location_id: l.location_id || null,
          expiry_date: l.expiry_date || null,
          remarks: l.remarks || null,
          line_number: i + 1,
        }));
      if (!payloadLines.length) throw new Error('Add at least one line with item and qty');
      return consignmentService.createReceipt({
        supplier_id: values.supplier_id,
        warehouse_id: values.warehouse_id,
        receipt_date: values.receipt_date.format('YYYY-MM-DD'),
        remarks: values.remarks || null,
        lines: payloadLines,
      });
    },
    onSuccess: (row) => {
      message.success('Receipt created');
      queryClient.invalidateQueries({ queryKey: ['consignment-receipts'] });
      navigate(`/scm/inventory/consignment-receipts/${row.id}`, { replace: true });
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Create failed'),
  });

  const postMut = useMutation({
    mutationFn: () => consignmentService.postReceipt(id!),
    onSuccess: () => {
      message.success('Receipt posted');
      queryClient.invalidateQueries({ queryKey: ['consignment-receipt', id] });
      queryClient.invalidateQueries({ queryKey: ['consignment-receipts'] });
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Post failed'),
  });

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card>
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <Space>
              <Link to="/scm/inventory/consignment-receipts">
                <Button type="text" icon={<ArrowLeftOutlined />} />
              </Link>
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {isNew ? 'New consignment receipt' : receipt?.receipt_number}
                </Title>
                {receipt && <Tag color={receipt.status === 'Posted' ? 'green' : 'gold'}>{receipt.status}</Tag>}
              </div>
            </Space>
            <Space>
              {isNew && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={createMut.isPending}
                  onClick={() => createMut.mutate()}
                >
                  Create
                </Button>
              )}
              {!isNew && receipt?.status === 'Draft' && (
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  loading={postMut.isPending}
                  onClick={() => postMut.mutate()}
                >
                  Post
                </Button>
              )}
            </Space>
          </Space>
        </Card>

        <Card title="Header">
          <Form
            form={form}
            layout="vertical"
            initialValues={{ receipt_date: dayjs() }}
            disabled={!isDraft || !isNew}
          >
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="supplier_id"
                  label="Supplier"
                  rules={[{ required: true, message: 'Supplier required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={suppliers.map((s) => ({
                      value: s.id,
                      label: s.supplier_code
                        ? `${s.supplier_code} — ${s.supplier_name}`
                        : s.supplier_name || s.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="warehouse_id"
                  label="Warehouse"
                  rules={[{ required: true, message: 'Warehouse required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={(warehouses as Array<{ id: string; warehouse_name?: string }>).map(
                      (w) => ({
                        value: w.id,
                        label: w.warehouse_name || w.id,
                      })
                    )}
                    onChange={() =>
                      setLines((prev) => prev.map((l) => ({ ...l, location_id: null })))
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="receipt_date"
                  label="Receipt date"
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="remarks" label="Remarks">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card
          title="Lines"
          extra={
            isNew ? (
              <Button icon={<PlusOutlined />} onClick={() => setLines((p) => [...p, emptyLine()])}>
                Add line
              </Button>
            ) : null
          }
        >
          {isNew ? (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {lines.map((row, idx) => (
                <Card
                  key={row.key}
                  size="small"
                  type="inner"
                  title={`Line ${idx + 1}`}
                  extra={
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={lines.length <= 1}
                      onClick={() => setLines((p) => p.filter((l) => l.key !== row.key))}
                    />
                  }
                >
                  <Row gutter={12}>
                    <Col xs={24} md={10}>
                      <Text type="secondary">Item</Text>
                      <ItemSelect
                        value={row.item_id || undefined}
                        style={{ width: '100%', marginTop: 4 }}
                        onChange={(itemId, option) => {
                          const item = (option as { item?: ItemLike } | undefined)?.item;
                          updateLine(row.key, {
                            item_id: itemId || '',
                            description: item
                              ? `${item.item_code || ''} ${item.item_name || ''}`.trim()
                              : row.description,
                            uom: item?.base_uom || row.uom || 'PCS',
                          });
                        }}
                      />
                    </Col>
                    <Col xs={8} md={4}>
                      <Text type="secondary">Qty</Text>
                      <InputNumber
                        min={0.0001}
                        style={{ width: '100%', marginTop: 4 }}
                        value={row.qty}
                        onChange={(v) => updateLine(row.key, { qty: Number(v) || 0 })}
                      />
                    </Col>
                    <Col xs={8} md={3}>
                      <Text type="secondary">UoM</Text>
                      <Input
                        style={{ marginTop: 4 }}
                        value={row.uom || 'PCS'}
                        onChange={(e) => updateLine(row.key, { uom: e.target.value })}
                      />
                    </Col>
                    <Col xs={8} md={3}>
                      <Text type="secondary">Heat</Text>
                      <Input
                        style={{ marginTop: 4 }}
                        value={row.heat_number || ''}
                        onChange={(e) => updateLine(row.key, { heat_number: e.target.value })}
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <Text type="secondary">Dim</Text>
                      <Input
                        style={{ marginTop: 4 }}
                        value={row.dimension || ''}
                        onChange={(e) => updateLine(row.key, { dimension: e.target.value })}
                      />
                    </Col>
                    <Col xs={12} md={8}>
                      <Text type="secondary">Bin</Text>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%', marginTop: 4 }}
                        disabled={!warehouseId}
                        options={binOptions}
                        value={row.location_id || undefined}
                        onChange={(v) => updateLine(row.key, { location_id: v || null })}
                        placeholder="Optional bin"
                      />
                    </Col>
                  </Row>
                </Card>
              ))}
            </Space>
          ) : (
            <Table
              rowKey={(r) => r.id || String(r.line_number)}
              dataSource={receipt?.lines || []}
              pagination={false}
              columns={[
                { title: '#', dataIndex: 'line_number', width: 56 },
                { title: 'Item', dataIndex: 'item_id', ellipsis: true },
                { title: 'Description', dataIndex: 'description', ellipsis: true },
                { title: 'Qty', dataIndex: 'qty', width: 90 },
                { title: 'UoM', dataIndex: 'uom', width: 70 },
                {
                  title: 'Heat',
                  dataIndex: 'heat_number',
                  width: 100,
                  render: (v: string) => v || '—',
                },
                {
                  title: 'Bin',
                  dataIndex: 'location_id',
                  width: 120,
                  render: (v: string) => {
                    if (!v) return 'Root';
                    const loc = locations.find((l) => l.id === v);
                    return loc?.location_code || v.slice(0, 8);
                  },
                },
              ]}
            />
          )}
        </Card>
      </Space>
    </div>
  );
};

export default ConsignmentReceiptDetailPage;
